import { Suspense, useEffect } from 'react';

import { AppShell } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet, useLocation } from 'react-router-dom';

import { useBelowBreakpoint } from '../../../theme/useBelowBreakpoint';
import { LogModal } from '../../LogModal';
import { RouteErrorBoundary } from '../../RouteErrorBoundary';
import { RouteFallback } from '../../RouteFallback';
import { AgentDock } from '../../agent/AgentDock';
import { useAgentPanelWidth } from '../../agent/agentPanel';
import { LayoutFooter } from './LayoutFooter';
import { LayoutHeader } from './LayoutHeader';
import { LayoutNavbar } from './LayoutNavbar';
import { useLayoutShortcuts } from './useLayoutShortcuts';

/** Mantine's own default, kept while nobody drags the agent panel's edge. */
const APP_SHELL_TRANSITION_MS = 200;

function LayoutContent() {
  const location = useLocation();
  const { logModalOpened, setLogModalOpened, uploadFeatureUnlocked, agentOpened, setAgentOpened } =
    useLayoutShortcuts();
  const agentPanelWidth = useAgentPanelWidth();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  // AppShell's `footer.collapsed` is a boolean, so this is one of the few
  // places that genuinely needs JS rather than a `hiddenFrom` prop: hiding the
  // footer with display:none instead leaves 76px of dead padding under every
  // page.
  const isMobile = useBelowBreakpoint('sm');

  // On mobile the navbar is a full-width overlay. This effect closes it on any real
  // route change (the upload shortcut, browser back/forward, and normal link taps).
  // Navigation controls also call closeMobile() directly in their onClick so that
  // re-tapping the already-active route, a no-op the effect can't see, still closes it.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Fits the longest group label ("Commitments") beside its icon and chevron without truncating.
  const navbarWidth = 228;

  return (
    <AppShell
      header={{ height: 64 }}
      footer={{ height: 60, collapsed: isMobile }}
      navbar={{
        width: navbarWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      aside={{
        width: agentPanelWidth.width,
        breakpoint: 'sm',
        collapsed: { mobile: !agentOpened, desktop: !agentOpened },
      }}
      padding="md"
      transitionDuration={agentPanelWidth.resizing ? 0 : APP_SHELL_TRANSITION_MS}
    >
      <LayoutHeader
        mobileOpened={mobileOpened}
        toggleMobile={toggleMobile}
        closeMobile={closeMobile}
      />
      <LayoutNavbar
        navbarWidth={navbarWidth}
        desktopOpened={desktopOpened}
        toggleDesktop={toggleDesktop}
        uploadFeatureUnlocked={uploadFeatureUnlocked}
        closeMobile={closeMobile}
      />

      <AppShell.Main style={{ minWidth: 0 }}>
        {/* Decorative only. On a phone 20px of it bleeds into the top-left of
            every page and sits over the first 20px of any full-bleed content,
            so it is not rendered there rather than hidden. There is no layout
            to preserve, it is absolutely positioned. */}
        {!isMobile && (
          <div style={{ position: 'relative' }}>
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                width: '100px',
                height: '100px',
                backgroundColor: 'var(--mantine-color-orange-6)',
                clipPath: 'polygon(0% 0%, 100% 0%, 0% 100%)',
                top: '-20px',
                left: '-80px',
              }}
            ></div>
          </div>
        )}
        <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </AppShell.Main>

      <LayoutFooter isMobile={isMobile} />

      <AgentDock
        opened={agentOpened}
        onMinimize={() => setAgentOpened(false)}
        panelWidth={agentPanelWidth}
      />

      <LogModal opened={logModalOpened} onClose={() => setLogModalOpened(false)} />
    </AppShell>
  );
}

export function Layout() {
  return <LayoutContent />;
}
